import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type ActiveCycleResponse,
  dueFromTerms,
  formatMoney,
  INSTALLMENT_KIND_LABELS,
  INSTALLMENT_KINDS,
  INSTALLMENT_REMINDER_DAYS,
  INSTALLMENT_STATE_LABELS,
  type InstallmentKind,
  type InstallmentState,
  type JobInstallment,
  type JobInstallmentRequest,
  type JobResponse,
  type LicenceConfig,
  parseDecimal,
} from '@k-management/shared';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { amountSort, SortHeader, useTableSort } from '../../components/SortHeader.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { moneyOf } from '../people/people-ui.js';
import { ClientPicker } from '../quotes/ClientPicker.js';
import { CreditNoteMark, creditNoteRow } from '../treasury/treasury-ui.js';
import { InvoiceMatches } from './InvoiceMatches.js';
import { jobKeys, MoveButtons, RowAction } from './jobs-ui.js';
import {
  draftsOf,
  type LineDraft,
  lineDraft,
  lineRequest,
  linesTotal,
  MissingNote,
  ModeChoice,
  RataLines,
  usePricing,
} from './RataLines.js';

const STATE_CLASS: Record<InstallmentState, string> = {
  prevista: 'muted',
  fatturabile: 'warn',
  parziale: 'info',
  fatturata: 'info',
  incassata: 'ok',
};

export const cycleKeys = {
  active: (id: string) => [...jobKeys.all, 'active-cycle', id] as const,
};

/**
 * The ciclo attivo of a commessa (2.03): the rate, each with its date of
 * emissione and of incasso, and the issued invoices given to it. A madre
 * shows those of its sottocommesse, read only.
 */
export function ActiveCycle({ job }: { job: JobResponse }) {
  const queryClient = useQueryClient();
  const canTreasury = useCan('tesoreria.read');
  const [editing, setEditing] = useState<JobInstallment | 'new' | null>(null);
  const [removing, setRemoving] = useState<JobInstallment | null>(null);
  // The rate «dalle tipologie» whose lines are open in the list (2.50).
  const [openLines, setOpenLines] = useState<string[]>([]);
  const cycle = useQuery({
    queryKey: cycleKeys.active(job.id),
    queryFn: () => apiRequest<ActiveCycleResponse>(`/jobs/${job.id}/active-cycle`),
  });
  const refresh = (data: ActiveCycleResponse) => {
    queryClient.setQueryData(cycleKeys.active(job.id), data);
    return queryClient.invalidateQueries({ queryKey: jobKeys.all });
  };
  const release = useMutation({
    mutationFn: ({ id, released }: { id: string; released: boolean }) =>
      apiRequest<ActiveCycleResponse>(`/jobs/${job.id}/installments/${id}/release`, {
        method: 'POST',
        body: { released },
      }),
    onSuccess: refresh,
  });
  const move = useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: 'up' | 'down' }) =>
      apiRequest<ActiveCycleResponse>(`/jobs/${job.id}/installments/${id}/move`, {
        method: 'POST',
        body: { direction },
      }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      apiRequest<ActiveCycleResponse>(`/jobs/${job.id}/installments/${id}`, { method: 'DELETE' }),
    onSuccess: async (data) => {
      setRemoving(null);
      await refresh(data);
    },
  });
  const installments = useTableSort('job.active.installments', cycle.data?.installments ?? [], {
    description: (row) => row.description,
    billingClient: (row) => row.billingClient.name,
    issueOn: (row) => row.issueOn,
    amount: (row) => amountSort(row.amount),
    collectOn: (row) => row.collectOn,
    state: (row) => INSTALLMENT_STATE_LABELS[row.state],
    invoiced: (row) => amountSort(row.invoiced),
  });
  const invoices = useTableSort('job.active.invoices', cycle.data?.invoices ?? [], {
    number: (row) => row.number,
    jobCode: (row) => row.jobCode,
    subject: (row) => row.subject,
    date: (row) => row.date,
    taxable: (row) => amountSort(row.taxable),
    amount: (row) => amountSort(row.amount),
    collected: (row) => amountSort(row.collected),
    nextDue: (row) => row.nextDue,
  });

  if (cycle.isError) return <p className="error">{cycle.error.message}</p>;
  if (!cycle.data) return <p className="muted">Caricamento…</p>;
  const data = cycle.data;
  const editable = job.you.edit && !data.readOnly;
  const late = data.installments.filter((row) => row.lateDays > 0);
  const totalVat = data.installments.reduce((sum, row) => sum + Number(row.vat), 0);

  return (
    <section className="stack">
      <div className="kpi-row">
        <Kpi label="Offerto" value={formatMoney(data.totals.offered)} />
        <Kpi
          label="Pianificato in rate"
          value={formatMoney(data.totals.planned)}
          note={
            data.totals.planned !== data.totals.offered && Number(data.totals.offered) !== 0
              ? `diverso dall’offerto di ${formatMoney(Number(data.totals.planned) - Number(data.totals.offered))}`
              : undefined
          }
        />
        <Kpi label="Fatturato (imponibile)" value={formatMoney(data.totals.invoiced)} />
        <Kpi label="Incassato (con IVA)" value={formatMoney(data.totals.collected)} />
        <Kpi label="Da incassare (con IVA)" value={formatMoney(data.totals.toCollect)} />
      </div>
      {late.map((row) => (
        <p key={row.id} className="notice">
          La rata «{row.description}»{data.readOnly && ` di ${row.jobCode}`} è{' '}
          <strong>fatturabile da {row.lateDays} giorni</strong> e non ha ancora la fattura (avviso
          dopo {INSTALLMENT_REMINDER_DAYS} giorni).
        </p>
      ))}
      {data.readOnly && (
        <p className="notice info">
          La commessa ha sottocommesse: qui la somma delle loro rate, in sola lettura. Le rate si
          gestiscono nelle sottocommesse.
        </p>
      )}

      <section className="card stack">
        <div className="row between">
          <h3>Piano di fatturazione</h3>
          {editable && (
            <button type="button" className="primary" onClick={() => setEditing('new')}>
              Nuova rata
            </button>
          )}
        </div>
        {data.installments.length === 0 ? (
          <p className="muted">
            Nessuna rata. Nascono dal piano di pagamento delle offerte collegate, oppure si
            aggiungono a mano.
          </p>
        ) : (
          <div className="table-scroll">
            <table className="lines">
              <thead>
                <tr>
                  <SortHeader sort={installments} column="description">
                    Rata
                  </SortHeader>
                  <SortHeader sort={installments} column="billingClient">
                    Si fattura a
                  </SortHeader>
                  <SortHeader sort={installments} column="issueOn">
                    Emissione
                  </SortHeader>
                  <SortHeader sort={installments} column="amount" className="right">
                    Imponibile
                  </SortHeader>
                  <SortHeader sort={installments} column="collectOn">
                    Incasso
                  </SortHeader>
                  <SortHeader sort={installments} column="state">
                    Stato
                  </SortHeader>
                  <SortHeader sort={installments} column="invoiced" className="right">
                    Fatturato
                  </SortHeader>
                  {editable && <th />}
                </tr>
              </thead>
              <tbody>
                {installments.rows.map((row, index) => (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.description}</strong>
                      {row.lines.length > 0 && (
                        <>
                          {' '}
                          <button
                            type="button"
                            className="link small"
                            onClick={() =>
                              setOpenLines((current) =>
                                current.includes(row.id)
                                  ? current.filter((id) => id !== row.id)
                                  : [...current, row.id],
                              )
                            }
                          >
                            {openLines.includes(row.id) ? '▾' : '▸'}{' '}
                            {row.lines.length === 1 ? '1 riga' : `${row.lines.length} righe`}
                          </button>
                        </>
                      )}
                      {openLines.includes(row.id) && (
                        <ul className="rata-line-list small">
                          {row.lines.map((line, index) => (
                            <li key={index}>
                              {line.label}
                              {line.isExpense && <span className="pill info">spesa</span>}
                              {line.typeCode !== 'LIC' && (
                                <span className="muted">
                                  {' · '}
                                  {line.editions > 1 && `${line.editions} edizioni × `}
                                  {Number(line.quantity).toLocaleString('it-IT')}{' '}
                                  {line.unitCode.toLowerCase()} × {formatMoney(line.unitPrice)}
                                </span>
                              )}{' '}
                              <strong>{formatMoney(line.net)}</strong>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="muted small">
                        {INSTALLMENT_KIND_LABELS[row.kind]}
                        {row.quote && (
                          <>
                            {' · '}
                            <Link to={`/offerte/${row.quote.id}`}>{row.quote.label}</Link>
                          </>
                        )}
                        {data.readOnly && (
                          <>
                            {' · '}
                            <Link to={`/commesse/${row.jobId}`}>{row.jobCode}</Link>
                          </>
                        )}
                      </div>
                    </td>
                    <td>{row.billingClient.name}</td>
                    <td className="nowrap">{formatDate(row.issueOn)}</td>
                    <td className="right nowrap">
                      {formatMoney(row.amount)}
                      <div className="muted small">
                        IVA {formatMoney(row.vat)}
                        {row.splitPayment && ' · split payment'}
                      </div>
                      {Number(row.expenses) !== 0 && (
                        <div className="small">
                          <span className="pill info">
                            {row.isExpense ? 'spesa' : `di cui spese ${formatMoney(row.expenses)}`}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="nowrap">
                      {formatDate(row.collectOn)}
                      {row.termsCode && <div className="muted small">{row.termsCode}</div>}
                    </td>
                    <td>
                      <span className={`pill ${STATE_CLASS[row.state]}`}>
                        {INSTALLMENT_STATE_LABELS[row.state]}
                      </span>
                      {row.releasedOn && (
                        <div className="muted small">
                          via libera {formatDate(row.releasedOn)}
                          {row.releasedBy && `, ${row.releasedBy}`}
                        </div>
                      )}
                    </td>
                    <td className="right nowrap">{formatMoney(row.invoiced)}</td>
                    {editable && (
                      <td className="row-actions">
                        <MoveButtons
                          index={index}
                          count={installments.rows.length}
                          sorted={installments.sort !== null}
                          pending={move.isPending}
                          onMove={(direction) => move.mutate({ id: row.id, direction })}
                        />
                        {row.kind === 'erogazione' &&
                          (row.state === 'prevista' ||
                            (row.state === 'fatturabile' && row.releasedOn)) && (
                            <RowAction
                              icon={row.releasedOn ? 'undo' : 'check'}
                              label={
                                row.releasedOn
                                  ? 'Togli il via libera: la rata torna prevista'
                                  : 'Fatturabile: dai il via libera'
                              }
                              disabled={release.isPending}
                              onClick={() =>
                                release.mutate({ id: row.id, released: !row.releasedOn })
                              }
                            />
                          )}
                        <RowAction icon="edit" label="Modifica" onClick={() => setEditing(row)} />
                        {Number(row.invoiced) === 0 && (
                          <RowAction
                            icon="trash"
                            label="Togli la rata"
                            onClick={() => setRemoving(row)}
                          />
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                <tr>
                  <td colSpan={3} className="muted">
                    Totale · IVA {formatMoney(totalVat)}
                  </td>
                  <td className="right nowrap">
                    <strong>{formatMoney(data.totals.planned)}</strong>
                  </td>
                  <td colSpan={2} />
                  <td className="right nowrap">{formatMoney(data.totals.invoiced)}</td>
                  {editable && <td />}
                </tr>
              </tbody>
            </table>
          </div>
        )}
        {release.isError && <p className="error">{release.error.message}</p>}
        {move.isError && <p className="error">{move.error.message}</p>}
        <p className="muted small">
          Prevista → fatturabile → fatturata → incassata. Una rata a data fissa diventa fatturabile
          alla data; una all’erogazione con il via libera (per ora del CFO, allineato con il PM).
          Importi e date si correggono finché la rata non ha la sua fattura.
        </p>
      </section>

      <section className="card stack">
        <h3>Fatture emesse della commessa</h3>
        {data.invoices.length === 0 ? (
          <p className="muted">Nessuna fattura imputata.</p>
        ) : (
          <div className="table-scroll">
            <table className="lines">
              <thead>
                <tr>
                  <SortHeader sort={invoices} column="number">
                    Fattura
                  </SortHeader>
                  {data.readOnly && (
                    <SortHeader sort={invoices} column="jobCode">
                      Sottocommessa
                    </SortHeader>
                  )}
                  <SortHeader sort={invoices} column="subject">
                    Intestatario
                  </SortHeader>
                  <SortHeader sort={invoices} column="date">
                    Data
                  </SortHeader>
                  <SortHeader sort={invoices} column="taxable" className="right">
                    Imponibile fattura
                  </SortHeader>
                  <SortHeader sort={invoices} column="amount">
                    Ripartizione
                  </SortHeader>
                  <SortHeader sort={invoices} column="collected" className="right">
                    Incassato
                  </SortHeader>
                  <SortHeader sort={invoices} column="nextDue">
                    Prossima scadenza
                  </SortHeader>
                </tr>
              </thead>
              <tbody>
                {invoices.rows.map((row) => (
                  <tr key={row.allocationId} className={creditNoteRow(row)}>
                    <td>
                      <CreditNoteMark kind={row.kind} />
                      {canTreasury ? (
                        <Link to={`/tesoreria/fatture/${row.invoiceId}`}>{row.number}</Link>
                      ) : (
                        row.number
                      )}
                    </td>
                    {data.readOnly && <td>{row.jobCode}</td>}
                    <td>{row.subject}</td>
                    <td>{formatDate(row.date)}</td>
                    <td className="right">{formatMoney(row.taxable)}</td>
                    <td>
                      {row.sharedWith.length === 0 && row.amount === row.taxable
                        ? 'tutta qui'
                        : `${formatMoney(row.amount)} qui`}
                      {row.lines.length > 0 && (
                        <small className="muted">
                          {' '}
                          · {row.lines.length === 1 ? 'riga' : 'righe'}{' '}
                          {row.lines.map((position) => position + 1).join(', ')}
                        </small>
                      )}
                      {row.toReview && (
                        <>
                          {' '}
                          <span
                            className="pill warn"
                            title="Una riga è cambiata in Gestify dopo l’imputazione: rivedi la ripartizione sulla fattura"
                          >
                            da rivedere
                          </span>
                        </>
                      )}
                      {row.sharedWith.map((other) => (
                        <small key={other.jobId} className="muted">
                          {' '}
                          · {formatMoney(other.amount)} su{' '}
                          <Link to={`/commesse/${other.jobId}`}>{other.code}</Link>
                        </small>
                      ))}
                    </td>
                    <td className="right">
                      {formatMoney(row.collected)}
                      <small className="muted"> di {formatMoney(row.due)}</small>
                    </td>
                    <td>
                      {row.nextDue ? (
                        formatDate(row.nextDue)
                      ) : (
                        <span className="pill ok">incassata</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editable && canTreasury && (
        <InvoiceMatches
          job={job}
          direction="emessa"
          targets={data.installments
            .filter((row) => Number(row.amount) - Number(row.invoiced) !== 0)
            .map((row) => ({
              id: row.id,
              label: `${row.description} (${formatDate(row.issueOn)})`,
              open: (Number(row.amount) - Number(row.invoiced)).toFixed(2),
              subjectIds: [row.billingClient.id],
            }))}
        />
      )}

      {editing && (
        <InstallmentDialog
          job={job}
          options={data.options}
          installment={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
      {removing && (
        <ConfirmDialog
          title={`Togliere la rata «${removing.description}»?`}
          confirmLabel="Togli"
          busy={remove.isPending}
          error={remove.isError ? remove.error.message : null}
          onConfirm={() => remove.mutate(removing.id)}
          onCancel={() => setRemoving(null)}
        >
          <p>
            {formatMoney(removing.amount)} con emissione il {formatDate(removing.issueOn)}. La
            rimozione resta nello storico.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}

function Kpi({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="card kpi">
      <small className="muted">{label}</small>
      <strong>{value}</strong>
      {note && <small className="muted">{note}</small>}
    </div>
  );
}

/** A rata, new or changed: the date of incasso proposed from the terms, editable. */
function InstallmentDialog({
  job,
  options,
  installment,
  onClose,
  onSaved,
}: {
  job: JobResponse;
  options: ActiveCycleResponse['options'];
  installment: JobInstallment | null;
  onClose: () => void;
  onSaved: (data: ActiveCycleResponse) => Promise<unknown>;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const [description, setDescription] = useState(installment?.description ?? '');
  const [kind, setKind] = useState<InstallmentKind>(installment?.kind ?? 'erogazione');
  // «Si fattura a» another subject: kept apart from the subject, which is empty while choosing.
  const [ownBilling, setOwnBilling] = useState(Boolean(installment?.billingOwn));
  const [billingClientId, setBillingClientId] = useState<string | null>(
    installment?.billingOwn ? installment.billingClient.id : null,
  );
  const [issueOn, setIssueOn] = useState(installment?.issueOn ?? job.startOn ?? '');
  const [termsCode, setTermsCode] = useState(installment?.termsCode ?? '');
  const [collectOn, setCollectOn] = useState(installment?.collectOn ?? '');
  const [collectTouched, setCollectTouched] = useState(Boolean(installment));
  const [amount, setAmount] = useState(installment?.amount ?? '');
  // «Dalle tipologie» or «libera» (2.50): a new rata starts from the types.
  const composedBefore = Boolean(installment?.lines.length || installment?.licences);
  const [mode, setMode] = useState<'lines' | 'free'>(
    !installment || composedBefore ? 'lines' : 'free',
  );
  const [lines, setLines] = useState<LineDraft[]>(() =>
    installment && composedBefore ? draftsOf(installment) : installment ? [] : [lineDraft()],
  );
  const [licences, setLicences] = useState<LicenceConfig | null>(installment?.licences ?? null);
  const [isExpense, setIsExpense] = useState(installment?.isExpense ?? false);
  const pricing = usePricing(job.id);
  // Once invoiced, the lines stay as they are (2.50).
  const locked = Boolean(installment) && parseDecimal(installment!.invoiced) !== 0;
  const total = linesTotal(lines, licences, pricing.data?.priceList).total;
  /**
   * A rata «libera» turned into one «dalle tipologie» starts from a line
   * «Altro» with its description and amount: nothing changes until edited.
   */
  const toLines = () => {
    setMode('lines');
    if (lines.length || licences || !pricing.data) return;
    // The general «Altro» when there is one, else the «Altro» of a type.
    const types = pricing.data.types;
    const other =
      types.find(
        (type) =>
          !type.parent && type.freeText && !types.some((child) => child.parent === type.code),
      ) ?? types.find((type) => type.parent && type.freeText);
    const corpo = pricing.data.units.find((unit) => unit.code === 'CORPO')?.code;
    setLines([
      lineDraft({
        typeCode: other?.parent ?? other?.code ?? '',
        subTypeCode: other?.parent ? other.code : '',
        description: description,
        quantity: '1',
        unitCode: corpo ?? pricing.data.units[0]?.code ?? '',
        unitPrice: moneyOf(amount) ?? '',
      }),
    ]);
  };
  const toFree = () => {
    setMode('free');
    if (lines.length || licences) setAmount(total);
  };
  const [vatRate, setVatRate] = useState(installment?.vatRate ?? '22.00');
  // Null on a new rata: the server takes it from the last invoice of «Si fattura a» (2.05).
  const [splitPayment, setSplitPayment] = useState<boolean | null>(
    installment?.splitPayment ?? null,
  );
  // Scadenzario (3.25): a rata from an accepted offer is certain; one written by hand says so here.
  const [contracted, setContracted] = useState(installment?.contracted ?? false);
  // The date of incasso follows emissione and terms until the user writes it.
  const proposed = (() => {
    const terms = options.terms.find((item) => item.code === termsCode);
    return issueOn && terms ? dueFromTerms(issueOn, terms.terms) : '';
  })();
  const shownCollect = collectTouched ? collectOn : proposed;
  const save = useMutation({
    mutationFn: (body: JobInstallmentRequest) =>
      installment
        ? apiRequest<ActiveCycleResponse>(`/jobs/${job.id}/installments/${installment.id}`, {
            method: 'PUT',
            body,
          })
        : apiRequest<ActiveCycleResponse>(`/jobs/${job.id}/installments`, {
            method: 'POST',
            body,
          }),
    onSuccess: async (data) => {
      await onSaved(data);
      onClose();
    },
  });
  const money = moneyOf(amount);
  const linesReady =
    (lines.length > 0 || licences !== null) &&
    lines.every(
      (line) =>
        line.typeCode &&
        line.unitCode &&
        (parseDecimal(line.quantity) ?? 0) > 0 &&
        parseDecimal(line.unitPrice) !== null,
    ) &&
    parseDecimal(total) !== 0;
  const ready =
    description.trim() &&
    issueOn &&
    (!ownBilling || billingClientId) &&
    (mode === 'lines' ? linesReady : money !== null && Number(money) !== 0);
  // What keeps Salva off, said in words (2.54).
  const missing = [
    !description.trim() && 'la descrizione',
    !issueOn && 'la data di emissione',
    ownBilling && !billingClientId && 'a chi si fattura',
    ...(mode === 'lines'
      ? [
          !lines.length && !licences && 'almeno una riga',
          lines.some((line) => !line.typeCode) && 'la tipologia di ogni riga',
          lines.some((line) => !line.unitCode) && 'l’unità di ogni riga',
          lines.some((line) => (parseDecimal(line.quantity) ?? 0) <= 0) &&
            'la quantità di ogni riga',
          lines.some((line) => parseDecimal(line.unitPrice) === null) && 'il prezzo di ogni riga',
          (lines.length > 0 || licences !== null) &&
            parseDecimal(total) === 0 &&
            'un imponibile diverso da zero',
        ]
      : [(money === null || Number(money) === 0) && 'l’imponibile']),
  ].filter((item): item is string => Boolean(item));

  return (
    <dialog ref={dialogRef} className="dialog rata-dialog" onClose={onClose}>
      <div className="stack">
        <h2>{installment ? `Rata «${installment.description}»` : 'Nuova rata'}</h2>
        <ModeChoice
          legend="Come si compone la rata"
          value={mode}
          onChange={(value) => (value === 'lines' ? toLines() : toFree())}
          options={[
            {
              value: 'lines',
              label: 'Righe come nel preventivo',
              hint: 'tipologie, quantità, prezzi, licenze',
              disabled: locked,
            },
            {
              value: 'free',
              label: 'Importo scritto a mano',
              hint: 'scrivi solo l’imponibile',
              disabled: locked && composedBefore,
            },
          ]}
        />
        <Field label="Descrizione">
          <input
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            maxLength={200}
          />
        </Field>
        <Field
          label="Tipo"
          hint={
            kind === 'data_fissa'
              ? 'Diventa fatturabile da sola alla data di emissione (anticipo, canoni).'
              : 'Diventa fatturabile con il via libera, quando la prestazione è erogata.'
          }
        >
          <select value={kind} onChange={(event) => setKind(event.target.value as InstallmentKind)}>
            {INSTALLMENT_KINDS.map((item) => (
              <option key={item} value={item}>
                {INSTALLMENT_KIND_LABELS[item]}
              </option>
            ))}
          </select>
        </Field>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={!ownBilling}
            onChange={(event) => setOwnBilling(!event.target.checked)}
          />{' '}
          Si fattura a {job.billingClient?.name ?? job.clientName}, come la commessa
        </label>
        {ownBilling && (
          <ClientPicker
            label="Si fattura a"
            value={billingClientId}
            onChange={setBillingClientId}
            returnTo={`/commesse/${job.id}`}
          />
        )}
        <div className="form-grid">
          <Field label="Emissione prevista">
            <input
              type="date"
              value={issueOn}
              onChange={(event) => setIssueOn(event.target.value)}
            />
          </Field>
          <Field label="Termini di pagamento" hint="Vuoto: quelli di «Si fattura a» in Anagrafica.">
            <select value={termsCode} onChange={(event) => setTermsCode(event.target.value)}>
              <option value="">Dall’Anagrafica</option>
              {options.terms.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.label}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Incasso previsto"
            hint={collectTouched ? undefined : 'Calcolato dai termini; si può cambiare.'}
          >
            <input
              type="date"
              value={shownCollect}
              onChange={(event) => {
                setCollectTouched(true);
                setCollectOn(event.target.value);
              }}
            />
          </Field>
          {mode === 'free' && (
            <Field label="Imponibile">
              <DecimalInput value={amount} onChange={setAmount} decimals={[2, 2]} />
            </Field>
          )}
          {mode === 'free' && (
            <label className="checkbox">
              <input
                type="checkbox"
                checked={isExpense}
                onChange={(event) => setIsExpense(event.target.checked)}
              />{' '}
              È una spesa (trasferte, vitto, rimborsi): fuori dal margine
            </label>
          )}
          <Field label="Aliquota IVA">
            <select value={vatRate} onChange={(event) => setVatRate(event.target.value)}>
              {[...new Map(options.vatRates.map((item) => [item.rate, item])).values()].map(
                (item) => (
                  <option key={item.code} value={item.rate}>
                    {item.label}
                  </option>
                ),
              )}
              {!options.vatRates.some((item) => item.rate === vatRate) && (
                <option value={vatRate}>{vatRate} %</option>
              )}
            </select>
          </Field>
        </div>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={splitPayment ?? false}
            onChange={(event) => setSplitPayment(event.target.checked)}
          />{' '}
          Split payment: il cliente paga solo l’imponibile, l’IVA la versa allo Stato
          {splitPayment === null && (
            <small className="muted"> (se non scegli: come l’ultima fattura del cliente)</small>
          )}
        </label>
        {installment?.quote ? (
          <p className="muted small">Da offerta: certa</p>
        ) : (
          <label className="checkbox">
            <input
              type="checkbox"
              checked={contracted}
              onChange={(event) => setContracted(event.target.checked)}
            />{' '}
            Contrattualizzata: il cliente l’ha accettata, entra nello scadenzario come certa
          </label>
        )}
        {mode === 'lines' && (
          <RataLines
            jobId={job.id}
            lines={lines}
            onLines={setLines}
            licences={licences}
            onLicences={setLicences}
            locked={locked}
          />
        )}
        {!ready && <MissingNote missing={missing} />}
        {save.isError && <p className="error">{save.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={!ready || save.isPending}
            onClick={() =>
              save.mutate({
                description,
                kind,
                billingClientId: ownBilling ? billingClientId : null,
                issueOn,
                collectOn: shownCollect || null,
                termsCode: termsCode || null,
                ...(mode === 'lines'
                  ? { lines: lines.map(lineRequest), licences, amount: null }
                  : { lines: null, licences: null, amount: money!, isExpense }),
                vatRate,
                splitPayment,
                contracted: installment?.quote ? false : contracted,
              })
            }
          >
            {save.isPending ? 'Attendere…' : 'Salva'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
